/**
 * Ordering for the member list.
 *
 * Rank first, name second: root, then operators, then registered accounts, then
 * anonymous guests. Within a rank the names are sorted the way the reader's
 * language sorts them, so Finnish puts ä and ö after z rather than beside a.
 */

/** Lower sorts higher up the list. */
const RANK = { root: 0, operator: 1, user: 2, guest: 3 };

/** Where a member sits in the pecking order. */
export function memberRank(member) {
  if (member?.role === 'root') return RANK.root;
  if (member?.role === 'operator') return RANK.operator;
  return member?.guest ? RANK.guest : RANK.user;
}

/**
 * Returns a new array, sorted. The input is left alone: it is the presence list
 * the server sent, and other code reads it in arrival order.
 *
 * `locale` is used for the name comparison; Intl.Collator handles the accented
 * letters that a plain < comparison gets wrong.
 */
export function sortMembers(members, locale = 'en') {
  const list = [...(members ?? [])];
  let compareNames;
  try {
    const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
    compareNames = (a, b) => collator.compare(a, b);
  } catch {
    // No Intl for this locale: fall back to a plain case-insensitive comparison.
    compareNames = (a, b) => a.toLowerCase().localeCompare(b.toLowerCase());
  }

  return list.sort((a, b) => {
    const byRank = memberRank(a) - memberRank(b);
    if (byRank !== 0) return byRank;
    return compareNames(String(a?.nickname ?? ''), String(b?.nickname ?? ''));
  });
}

/*
 * Keeping the member list up to date between snapshots.
 *
 * Every one of these goes by `key` - the identity the server folds a person's
 * sessions under, sent on each entry - and never by `peerId`. A member list
 * holds one entry per person, and which of their connections lends the entry
 * its peerId changes as their sessions come and go, so a list kept by peerId
 * loses track of anyone whose connection is replaced. That is what filled the
 * list with copies of whoever had a shaky network: their new connection is in
 * the room before the old one has been reaped, so the events that follow name
 * a session the list has never seen, nothing matches, the dead entry stays,
 * and the next arrival lands beside it. Twice for two drops, and so on.
 *
 * Each returns a new array and leaves the input alone.
 */

/** Someone arrived, or their entry is being replaced wholesale. */
export function applyJoin(members, member) {
  if (!member?.key) return [...(members ?? [])];
  return [...(members ?? []).filter((m) => m.key !== member.key), member];
}

/** Someone's last session left. */
export function applyLeave(members, key) {
  return (members ?? []).filter((m) => m.key !== key);
}

/**
 * Someone's entry changed - a camera switched on, or a second session of theirs
 * ended and the merged entry was recalculated. An update for a person the list
 * has lost track of is treated as an arrival rather than dropped, so a list that
 * has drifted repairs itself at the next thing that happens.
 */
export function applyUpdate(members, member) {
  if (!member?.key) return [...(members ?? [])];
  const list = members ?? [];
  return list.some((m) => m.key === member.key)
    ? list.map((m) => (m.key === member.key ? member : m))
    : [...list, member];
}

/** Someone took a new nickname, which is to say a new identity. */
export function applyRename(members, { fromKey, toKey, to }) {
  return (members ?? []).map((m) => (m.key === fromKey ? { ...m, key: toKey, nickname: to } : m));
}

export default { sortMembers, memberRank, applyJoin, applyLeave, applyUpdate, applyRename };
